import React, { useCallback, useMemo, useState } from 'react';
import { Button, Color, Icon, Input, Select, Text } from '@bb/elements-web';
import { validateAddress } from 'api/SupraController';
import styled from 'styled-components';
import { getStateCodesForCountry } from '../../../../../constants/address';
import {
  CanManageBillingProfileType,
  useSubscriptionsPageContext,
} from '../../SubscriptionsPageContext';
import { CardStatus } from './CardStatus';

type BillingAddressCardProps = {
  /** Whether the user can manage billing details. */
  canManageBilling: CanManageBillingProfileType;
};

/**
 * The "Address" section within the Billing Contact Details card.
 * User/Brokerage can view & update their physical address from here.
 */
const BillingAddressCard: React.FC<BillingAddressCardProps> = ({
  canManageBilling,
}) => {
  const {
    billingProfile,
    updateBillingProfile,
    isFetchingProfile,
    isUpdatingProfile,
    hasSavedCard,
  } = useSubscriptionsPageContext();

  const [updatedAddress1, setUpdatedAddress1] = useState('');
  const [updatedAddress2, setUpdatedAddress2] = useState('');
  const [updatedCity, setUpdatedCity] = useState('');
  const [updatedState, setUpdatedState] = useState('');
  const [updatedZip, setUpdatedZip] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [isValidatingAddress, setIsValidatingAddress] = useState(false);
  const [updateProfileError, setUpdateProfileError] = useState<Error>();

  /** State/province options for the dropdown, driven by the selected country. */
  const stateOptions = useMemo(
    () =>
      getStateCodesForCountry(selectedCountry).map(code => ({
        label: code,
        value: code,
      })),
    [selectedCountry],
  );

  /** Label for the state/province field — matches the country's terminology. */
  const stateFieldLabel = selectedCountry === 'CA' ? 'Province' : 'State';

  const formattedAddress = useMemo(() => {
    const address = [
      billingProfile?.Address1,
      billingProfile?.Address2,
      billingProfile?.City,
      billingProfile?.State,
      billingProfile?.Zip,
      billingProfile?.Country,
    ]
      .filter(str => str)
      .join(', ');
    return address;
  }, [billingProfile]);

  /** Whether or not they have filled in all the fields */
  const isCompleteAddress = useMemo(
    () =>
      updatedAddress1 &&
      updatedCity &&
      updatedState &&
      updatedZip &&
      selectedCountry,
    [selectedCountry, updatedAddress1, updatedCity, updatedState, updatedZip],
  );

  // Define some simple methods to handle actions in the form

  /** Switch the UI from displaying the address to editing the address */
  const switchToEditMode = useCallback(() => {
    if (canManageBilling.response) {
      setUpdatedAddress1(billingProfile?.Address1 || '');
      setUpdatedAddress2(billingProfile?.Address2 || '');
      setUpdatedCity(billingProfile?.City || '');
      setUpdatedState(billingProfile?.State || '');
      setUpdatedZip(billingProfile?.Zip || '');
      if (billingProfile?.Country) {
        setSelectedCountry(billingProfile?.Country || '');
      }
      setIsInEditMode(true);
    }
  }, [canManageBilling, billingProfile]);

  /** Switch the UI from editing the address to displaying the static address */
  const resetAndCloseEditMode = useCallback(() => {
    setUpdatedAddress1('');
    setUpdatedAddress2('');
    setUpdatedCity('');
    setUpdatedState('');
    setUpdatedZip('');
    setSelectedCountry('');
    setIsInEditMode(false);
    setUpdateProfileError(undefined);
  }, []);

  const updateAddress = useCallback(async () => {
    /** Whether this is a real location in the US or CA */
    async function isValidAddress() {
      if (!selectedCountry) return false;

      return validateAddress({
        city: updatedCity,
        stateCode: updatedState,
        zipCode: updatedZip,
        countryCode: selectedCountry,
      });
    }
    if (!canManageBilling.response) return;

    setIsValidatingAddress(true);
    setUpdateProfileError(undefined);
    try {
      if (!isCompleteAddress) return;

      if (!(await isValidAddress())) {
        setUpdateProfileError(
          new Error(
            'This address could not be verified. Please double-check the ' +
              'state/province, city, and postal/zip code.',
          ),
        );
        return;
      }

      const updatedProfile = {
        ...billingProfile,
        Address1: updatedAddress1,
        Address2: updatedAddress2,
        City: updatedCity,
        State: updatedState,
        Zip: updatedZip,
        Country: selectedCountry,
      };

      await updateBillingProfile(updatedProfile);
      resetAndCloseEditMode();
    } catch (error) {
      setUpdateProfileError(
        error instanceof Error ? error : new Error(String(error)),
      );
    } finally {
      setIsValidatingAddress(false);
    }
  }, [
    canManageBilling.response,
    isCompleteAddress,
    billingProfile,
    resetAndCloseEditMode,
    selectedCountry,
    updateBillingProfile,
    updatedAddress1,
    updatedAddress2,
    updatedCity,
    updatedState,
    updatedZip,
  ]);

  return (
    <StyledBody>
      {isInEditMode ? (
        <EditContainer>
          {updateProfileError && (
            <StyledCardStatus
              status="error"
              text={updateProfileError.message}
            />
          )}
          <EditTitleContainer>
            <StyledHeader color="SECONDARY_TEXT">Country</StyledHeader>
            <Button
              size="middle"
              shape="round"
              type="secondary"
              dataCy="discard-email-changes"
              styleOverride={CloseCircleButtonStyle}
              onClick={resetAndCloseEditMode}
            >
              <Icon icon={'closeOutlined'} size={16} color="black" />
            </Button>
          </EditTitleContainer>
          <VerticalStack>
            <StyledSelect
              width={'15%'}
              size="small"
              value={selectedCountry || undefined}
              key="countrySelect"
              placeholder="Country"
              disabled={isFetchingProfile}
              options={['CA', 'US'].map(country => ({
                label: country,
                value: country,
              }))}
              onChange={value => {
                setSelectedCountry(value as string);
                // The previously selected state/province is unlikely to be
                // valid for the new country, so clear it.
                setUpdatedState('');
              }}
              dataCy="country-select"
            />

            <StyledLabel>Address 1</StyledLabel>
            <Input
              onChange={setUpdatedAddress1}
              placeholder="Street Address"
              value={updatedAddress1}
              disabled={isFetchingProfile}
              required
              width="50%"
              dataCy="address-1-input-field"
            />
            <StyledLabel>Address 2</StyledLabel>
            <Input
              onChange={setUpdatedAddress2}
              placeholder="Apt, suite (optional)"
              value={updatedAddress2}
              disabled={isFetchingProfile}
              width="50%"
              dataCy="address-2-input-field"
            />
            <HorizontalStack>
              <VerticalStack>
                <StyledLabel>City</StyledLabel>
                <Input
                  onChange={setUpdatedCity}
                  value={updatedCity}
                  disabled={isFetchingProfile}
                  dataCy="city-input-field"
                />
              </VerticalStack>
              <VerticalStack>
                <StyledLabel>{stateFieldLabel}</StyledLabel>
                <StateSelect
                  width={'100%'}
                  size="small"
                  value={updatedState || undefined}
                  placeholder={stateFieldLabel}
                  disabled={isFetchingProfile || !selectedCountry}
                  options={stateOptions}
                  onChange={value => setUpdatedState(value as string)}
                  dataCy="state-select"
                />
              </VerticalStack>
            </HorizontalStack>
            <StyledLabel>
              {selectedCountry === 'CA' ? 'Postal Code' : 'Zip Code'}
            </StyledLabel>
            <StyledInput
              onChange={setUpdatedZip}
              value={updatedZip}
              disabled={isFetchingProfile}
              width="20%"
              dataCy="zip-input-field"
            />
          </VerticalStack>
          <Button
            size="middle"
            loading={isUpdatingProfile || isValidatingAddress}
            disabled={
              !isCompleteAddress || !canManageBilling.response || !hasSavedCard
            }
            dataCy="save-button"
            styleOverride={SaveButtonWidth}
            onClick={updateAddress}
          >
            Save
          </Button>
        </EditContainer>
      ) : (
        <>
          <DetailsContainer>
            <StyledHeader color="SECONDARY_TEXT">Address</StyledHeader>
            {formattedAddress ? (
              <StyledDescription>{formattedAddress}</StyledDescription>
            ) : (
              <StyledDescription>Not provided</StyledDescription>
            )}
          </DetailsContainer>
          <StyledButton
            onClick={switchToEditMode}
            type="secondary"
            minWidth={100}
            disabled={
              !canManageBilling.response || !hasSavedCard || isUpdatingProfile
            }
            dataCy="address-update-btn"
          >
            {'Update'}
          </StyledButton>
        </>
      )}
    </StyledBody>
  );
};

export default BillingAddressCard;

const StyledCardStatus = styled(CardStatus)`
  margin: 8px 0;
`;

const StyledLabel = styled(Text)`
  color: ${Color.SECONDARY_TEXT};
  margin-bottom: 8px;
  margin-top: 16px;
`;

const VerticalStack = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  width: 100%;
`;

const HorizontalStack = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 16px;
  width: 50%;
`;

const StyledSelect = styled(Select)`
  margin-top: 0px;
`;

const StateSelect = styled(Select)`
  width: 100%;
`;

const StyledButton = styled(Button)`
  &:disabled {
    background-color: ${Color.LIGHTER_GREY};
    color: ${Color.BORDER_GREY};
    border: none;
  }
`;

const StyledBody = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const EditContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const StyledHeader = styled(Text)`
  margin-bottom: 8px;
`;

const StyledInput = styled(Input)`
  margin-bottom: 8px;
`;

const StyledDescription = styled(Text)`
  color: ${Color.PRIMARY_TEXT};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px;
`;

const EditTitleContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: top;
`;

const CloseCircleButtonStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  maxWidth: 32,
  minWidth: 32,
  gap: 8,
  padding: 0,
  borderColor: Color.BORDER_GREY,
  marginLeft: 24,
};

const SaveButtonWidth = {
  width: 100,
  height: 40,
  maxWidth: 100,
  minWidth: 73,
};
